/* eslint-disable no-await-in-loop */
import { BaseInfoCompProps, useModePanelStyle, FontIcon, useModeFormStyle, capitalizeAll, BaseInfoCompActionsSet, BaseInfoCompSaveAction,
    getErrorMessage } from '@moderepo/mode-app-components';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Paper, Stepper, Step, StepLabel, StepContent, Button } from '@material-ui/core';
import clsx from 'clsx';
import produce, { castDraft } from 'immer';
import { modeUIContext, setIsLoading, showErrorNotification, showSuccessNotification } from '@moderepo/mode-ui-state';
import { ApiError, DeviceConfigFirmwareSchema, Entity, KeyValuePair, ProjectDevice, RitzAPI } from '@moderepo/mode-internal-apis';
import { AppAPI } from '@moderepo/mode-apis';
import { clearDeviceConfigSchemas, clearEntities, clearHomeDevices, fetchAllDeviceKVStores, setEntity, setHomeDevice, userAppDataContext }
    from '@moderepo/mode-data-state-user-app';
import { clearProjectDevices, ritzDataContext, setProjectDevice } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectAllDeviceKVStores, useSelectDeviceConfigSchemas, useSelectProject } from '../../../utils/dataStateHooks';
import { DeviceRegistrationConfigureDeviceStep, DeviceRegistrationCreateDeviceStep, DeviceRegistrationDownloadDeviceConfigStep,
    useDeviceRegistrationWizardStyle } from '.';
import { DeviceRegistrationWizardSettings } from '../../../utils/ritzUIExtensionUtils';
import { ENTITY_DEVICE_ID_ATTRIBUTE } from '../..';
import { DeviceConfigInputs } from '../../../components/devices/utils';
import { createDeviceConfigInputs, createUpdateDeviceConfigDDPActions, deleteUnusedDDPs, isDDPInstanceOfParamSet } from '../utils';



enum RegisterDeviceStep {
    CREATE_DEVICE = 'info',
    CONFIGURE_DEVICE = 'metrics_condition_targets',
    DOWNLOAD_DEVICE_CONFIG = 'metrics_condition_conditions',
}


interface StepConfig {
    readonly name: RegisterDeviceStep;
    readonly label: string;
}


const allSteps: StepConfig[] = [{
    name : RegisterDeviceStep.CREATE_DEVICE,
    label: 'device_registration_wizard.step_create_device.title',
}, {
    name : RegisterDeviceStep.CONFIGURE_DEVICE,
    label: 'device_registration_wizard.step_configure_device.title',
}, {
    name : RegisterDeviceStep.DOWNLOAD_DEVICE_CONFIG,
    label: 'device_registration_wizard.step_download_device_config.title',
}];



/**
 * The structure for the state where all the user inputs for all steps are stored.
 */
interface DeviceRegistrationUserInputs {
    readonly stepCreateDevice: {
        readonly deviceClass: string | undefined;
        readonly homeId: number | undefined;
        readonly channel: string | undefined;
        readonly deviceName: string | undefined;
        readonly timeZone: string | undefined;
    };
    readonly stepConfigureDevice: {
    };
    readonly stepDownloadConfig: {
    };
}


/**
 * Create a new DeviceRegistrationUserInputs object.
 */
const convertDeviceObjectToInputs = (
    homeId: number | undefined, device?: ProjectDevice | undefined, entity?: Entity | undefined,
): DeviceRegistrationUserInputs => {
    if (device) {
        return {
            stepCreateDevice: {
                deviceClass: device.deviceClass,
                homeId     : device.homeId,
                deviceName : device.name,
                channel    : device.channel,
                timeZone   : entity?.timeZone ?? (new Intl.DateTimeFormat()).resolvedOptions().timeZone,
            },
            stepConfigureDevice: {
            },
            stepDownloadConfig: {
            },
        };
    }
    return {
        stepCreateDevice: {
            deviceClass: undefined,
            homeId,
            deviceName : undefined,
            channel    : undefined,
            timeZone   : (new Intl.DateTimeFormat()).resolvedOptions().timeZone,
        },
        stepConfigureDevice: {
        },
        stepDownloadConfig: {
        },
    };
};


/**
 * Create a new Device using the provided params
 * NOTE: If we have "existingDevice" then don't create new one, only make API call to update the missing attributes
 */
const createOrUpdateDevice = async (
    projectId: number, deviceClass: string, homeId: number, channelName: string, deviceName: string, existingDevice: ProjectDevice | undefined,
): Promise<{
    readonly device: ProjectDevice;
    readonly deviceModified: boolean;
    readonly deviceChannelModified: boolean;
}> => {

    // If !existingDevice, create a new device and then call updateDevice to set the channel and homeId
    const newDevice = !existingDevice ? await RitzAPI.getInstance().createProjectDevice(projectId, deviceClass, deviceName) : existingDevice;
    let deviceModified = newDevice !== existingDevice;
    const deviceChannelModified = existingDevice !== undefined && existingDevice.channel !== channelName;

    // We have to use Ritz API to update the device's channel if the device does not have a channel set
    if (newDevice.channel !== channelName || newDevice.name !== deviceName) {
        await RitzAPI.getInstance().updateProjectDevice(projectId, newDevice.id, {
            channel: newDevice.channel !== channelName ? channelName : undefined,
            name   : newDevice.name !== deviceName ? deviceName : undefined,
        });
        deviceModified = true;
    }

    // Use Maze API to update the device's home if the device does not have a homeId set
    if (newDevice.homeId !== homeId) {
        await AppAPI.getInstance().updateHomeDevice(newDevice.id, {
            homeId,
        });
        deviceModified = true;
    }

    // Because we make different API calls to update the device therefore the "newDevice" might not have the latest data so we need to
    // refetch the device data to get the most up to date object. We only need to do this if
    // - There is no existingDevice which mean "newDevice" is newly created therefore "newDevice" object is missing "channel" and "homeId"
    // - There is an existing device and the device has been modified
    const latestDeviceObject = !existingDevice || deviceModified
        ? await RitzAPI.getInstance().getProjectDeviceById(projectId, newDevice.id)
        : newDevice;


    // We didn't change the newDevice's channel or homeId which mean it should have everything we needed so we can return it.
    return {
        device: latestDeviceObject,
        deviceModified,
        deviceChannelModified,
    };
};



/**
 * Create a new Entity using the provided params.
 * NOTE: If we have "existingEntity" then don't create new one, only make API call to update the missing attributes
 */
const createOrUpdateEntity = async (
    projectId: number, deviceId: number, homeId: number, deviceName: string,
    entityClass: string, timeZone: string, existingEntity: Entity | undefined,
): Promise<{
    readonly entity: Entity;
    readonly entityModified: boolean;
}> => {

    // If there isn't an "existingEntity", create a new entity and give it the ID based on the newDevice's ID.
    const newEntity = !existingEntity
        ? await AppAPI.getInstance().createEntity(projectId, {
            entityClass,
            entityId       : deviceId.toString(),
            displaySettings: {
                name: deviceName,
            },
            homeId,
            timeZone,
            attributes: {
                [ENTITY_DEVICE_ID_ATTRIBUTE]: deviceId,
            },
        })
        : existingEntity;
    let entityModified = newEntity !== existingEntity;

    // If the entity does not have an attribute _deviceId_ yet, add it to the entity.
    if (!newEntity.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE] || newEntity.displaySettings.name !== deviceName) {
        await AppAPI.getInstance().updateEntity(projectId, newEntity.entityId, {
            displaySettings: newEntity.displaySettings.name !== deviceName ? {
                name: deviceName,
            } : undefined,
            attributes: !newEntity.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE] ? {
                [ENTITY_DEVICE_ID_ATTRIBUTE]: deviceId,
            } : undefined,
        });

        entityModified = true;
    }

    return {
        entity: newEntity,
        entityModified,
    };
};



/**
 * Create a device and entity using the provided params.
 * NOTE: If we have "existingDevice" or "existingEntity" then don't create new one, only make API call to update the missing attributes
 */
const createDeviceAndEntity = async (
    projectId: number, deviceClass: string, homeId: number, channelName: string, deviceName: string, timeZone: string, entityClass: string,
    existingDevice: ProjectDevice | undefined, existingEntity: Entity | undefined,
): Promise<{
    readonly device: ProjectDevice;
    readonly deviceModified: boolean;
    readonly deviceChannelModified: boolean;
    readonly entity: Entity;
    readonly entityModified: boolean;
}> => {
    // Create a new device and entity or update existing one
    const { device, deviceModified, deviceChannelModified } = await createOrUpdateDevice(
        projectId, deviceClass, homeId, channelName, deviceName, existingDevice,
    );
    const { entity, entityModified } = await createOrUpdateEntity(
        projectId, device.id, homeId, deviceName, entityClass, timeZone, existingEntity,
    );

    return {
        device,
        deviceModified,
        deviceChannelModified,
        entity,
        entityModified,
    };
};



/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RegisterDeviceWizardActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<void>;
}



/**
 * Props required for DeviceRegistrationWizardComp
 */
export interface DeviceRegistrationWizardCompProps extends Omit<BaseInfoCompProps, 'fields'> {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // The device/entity that is being edited. This is only provided if the wizard is being used for editing an existing device and entity
    readonly targetDevice?: ProjectDevice | undefined;
    readonly targetEntity?: Entity | undefined;

    readonly preselectedHomeId?: number | undefined;
    readonly deviceRegistrationWizardSettings: DeviceRegistrationWizardSettings;

    // Override the BaseInfoCompProps's actions props
    readonly actions?: RegisterDeviceWizardActionsSet | undefined;
}



/**
 * This is the Register Device Wizard UI
 */
export const DeviceRegistrationWizardComp: React.FC<DeviceRegistrationWizardCompProps> = (props: DeviceRegistrationWizardCompProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { dispatch:dataDispatch } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const sharedClasses = useDeviceRegistrationWizardStyle();
    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();


    // The state to keep track of the current Device/Entity that is being edited. Initialize it with the targetDevice and targetEntity
    // if there is one
    const [currentDeviceAndEntity, setCurrentDeviceAndEntity] = useState<{
        readonly device?: ProjectDevice | undefined;
        readonly entity?: Entity | undefined;
    }>({
        device: props.targetDevice,
        entity: props.targetEntity,
    });


    // targetDevice and targetEntity might be loaded AFTER the "currentDevice" and "currentEntity" is initialized therefore we need to
    // update the "currentDevice" and "currentEntity" once "targetDevice" and "targetEntity" are loaded.
    useEffect(() => {
        if (props.targetDevice) {
            setCurrentDeviceAndEntity((currentValue) => {
                return produce(currentValue, (draft) => {
                    draft.device = props.targetDevice;
                });
            });
        }
    }, [props.targetDevice]);

    useEffect(() => {
        if (props.targetEntity) {
            setCurrentDeviceAndEntity((currentValue) => {
                return produce(currentValue, (draft) => {
                    draft.entity = castDraft(props.targetEntity);
                });
            });
        }
    }, [props.targetEntity]);


    // The state to keep track of the current step.
    const [currentStep, setCurrentStep] = useState<number>(0);


    // The states where all the user input values are saved after each step is completed
    const [registerDeviceInputs, setRegisterDeviceInputs] = useState<DeviceRegistrationUserInputs>(
        convertDeviceObjectToInputs(props.preselectedHomeId, currentDeviceAndEntity.device, currentDeviceAndEntity.entity),
    );


    /**
     * This useEffect will monitor the currentDeviceAndEntity's "device" and "entity" changes and update the "registerDeviceInputs"
     */
    useEffect(() => {
        setRegisterDeviceInputs(convertDeviceObjectToInputs(props.preselectedHomeId, currentDeviceAndEntity.device, currentDeviceAndEntity.entity));
    }, [currentDeviceAndEntity.device, currentDeviceAndEntity.entity, props.preselectedHomeId]);


    // To keep track of the errors for each step so we know which step has error
    const [errorsByStepName, setErrorsByStepName] = useState<{[step: string]: boolean}>({});


    // Fetch all device config schemas and DDP for the target device if a device is selected.
    const allDeviceConfigSchemas = useSelectDeviceConfigSchemas(currentDeviceAndEntity.device?.id, false, true, true);
    const allDDPs = useSelectAllDeviceKVStores(currentDeviceAndEntity.device?.id);



    /**
     * Once we have loaded the device config schema and DDPs, we can create device config objects which can be passed to the
     * DeviceConfigComp to be used for the input fields.
     * Because a device can have multiple device config schema, we will create 1 input object for each device config schema.
     * This will return an array of object like this
     * [
     *    [
     *        [
     *            {
     *                "enabledOTAUBootCheck": true,
     *                "objectParam": {
     *                    "x": "34916",
     *                    "y": "abcdef"
     *                },
     *                "sshPrivateKeyFilePath": "/etc/path",
     *                "tsdbBulkUploadLabel": "label"
     *            }
     *        ],
     *        [
     *            {
     *                "interval": 15,
     *                "peripheralDriverName": "xxx"
     *            }
     *        ]
     *    ]
     * ]
     */
    const [deviceConfigInputsForAllSchemas, setDeviceConfigInputsForAllSchemas] = useState<readonly DeviceConfigInputs[] | undefined>();

    useEffect(() => {
        if (allDDPs && allDeviceConfigSchemas && currentDeviceAndEntity.device) {
            const deviceId = currentDeviceAndEntity.device.id;
            const result: DeviceConfigInputs[] = [];

            allDeviceConfigSchemas.forEach((schema) => {
                if (schema.deviceConfigSchema) {
                    const deviceConfigInputsForOneSchema = createDeviceConfigInputs(deviceId, schema.deviceConfigSchema, allDDPs);
                    result.push(deviceConfigInputsForOneSchema);
                }
            });

            setDeviceConfigInputsForAllSchemas(result);
        }
    }, [allDDPs, allDeviceConfigSchemas, currentDeviceAndEntity.device, currentDeviceAndEntity.device?.id]);



    /**
     * On Step click, we can take the user directly to that step BUT we will only allow the user to go to a specific step when the user finished
     * all the steps
     */
    const onStepClick = useCallback((step: number) => {
        if (currentStep >= allSteps.length) {
            setCurrentStep(step);
        }
    }, [currentStep]);


    /**
     * Helper function to clean up a set of existing DDPs for a device based on the array of schemas that has been deleted.
     * We will compile a list of DDPs within the existingDDPs that were used for storing the device configuration and then call
     * the "deleteUnusedDDPs" to delete them. NOTE that not all DDPs in the "existingDDPs" needs to be deleted because some DDP might be
     * used for something else.
     */
    const cleanUpDDPs = useCallback(async (
        deviceId: number,
        existingDDPs: readonly KeyValuePair[] | undefined,
        deletedSchemas: readonly DeviceConfigFirmwareSchema[] | undefined,
    ) => {
        if (existingDDPs && deletedSchemas) {
            const ddpKeysToBeDeleted: string[] = [];

            // Loop through each schema in the deletedSchemas array
            deletedSchemas.forEach((schema) => {
                // Loop through each paramSet in the schema
                schema.deviceConfigSchema?.forEach((paramSet) => {
                    // try to match the DDP with the paramSet. If a DDP is being used for holding the configuration for a paramSet then
                    // add it to the list of DDPs to be deleted.
                    existingDDPs.forEach((ddp) => {
                        if (isDDPInstanceOfParamSet(ddp, paramSet)) {
                            ddpKeysToBeDeleted.push(ddp.key);
                        }
                    });
                });
            });

            if (ddpKeysToBeDeleted.length > 0) {
                try {
                    await deleteUnusedDDPs(deviceId, ddpKeysToBeDeleted, trans, uiDispatch, appDataDispatch);
                } catch (error) {
                    uiDispatch(showErrorNotification((error as ApiError).code));
                }
            }
        }
    }, [appDataDispatch, trans, uiDispatch]);


    /**
     * On Step Create Device completed
     */
    const onStepCreateDeviceCompleted = useCallback(async (
        deviceClass: string, homeId: number, channelName: string, deviceName: string, timeZone: string,
    ) => {
        // Find the entityClass we need to instantiate based on the provided deviceClass configured in the 'deviceRegistrationWizardSettings'
        const entityClass = props.deviceRegistrationWizardSettings.deviceClassesSettings.find((settings) => {
            return settings.deviceClass === deviceClass;
        })?.entityClass;

        if (entityClass) {
            uiDispatch(setIsLoading(true));
            try {
                const { device, deviceModified, deviceChannelModified, entity, entityModified } = await createDeviceAndEntity(
                    props.projectId, deviceClass, homeId, channelName, deviceName, timeZone, entityClass,
                    currentDeviceAndEntity.device, currentDeviceAndEntity.entity,
                );

                // Save the new device and entity in the "currentDeviceAndEntity".
                setCurrentDeviceAndEntity((currentValue) => {
                    return produce(currentValue, (draft) => {
                        draft.device = device;
                        draft.entity = castDraft(entity);
                    });
                });

                // Some times calling the "createDeviceAndEntity" will create new Device/Entity and some times it just update an existing
                // device/entity. If new device/entity is created then we need to clear the data state so that it will trigger an API call
                // to fetch new list of devices/entities.
                if (deviceModified) {
                    // Clear home device
                    await appDataDispatch(clearHomeDevices(homeId));
                    const updatedHomeDevice = await AppAPI.getInstance().getHomeDeviceById(device.id);
                    await appDataDispatch(setHomeDevice(homeId, updatedHomeDevice));

                    // clear and reload project device
                    await dataDispatch(clearProjectDevices(props.projectId));
                    const updatedProjectDevice = await RitzAPI.getInstance().getProjectDeviceById(props.projectId, device.id);
                    await dataDispatch(setProjectDevice(updatedProjectDevice));
                }

                if (entityModified) {
                    await appDataDispatch(clearEntities(props.projectId));
                    const updatedEntity = await AppAPI.getInstance().getEntityById(props.projectId, entity.entityId);
                    await appDataDispatch(setEntity(props.projectId, updatedEntity));
                }

                if (deviceModified || entityModified) {
                    uiDispatch(showSuccessNotification('device_registration_wizard.notif.device_entity_created_successfully'));
                }

                if (deviceChannelModified) {
                    // If the device's channel changed, it will have a different set of Device Config Schemas. This means we can delete all
                    // the DDPs created for the previous schemas
                    await cleanUpDDPs(device.id, allDDPs, allDeviceConfigSchemas);

                    // Clear the cached schemas associated with the device since they are no longer belong to the device.
                    await appDataDispatch(clearDeviceConfigSchemas(device.id));
                }

            } catch (error) {
                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
            }
            uiDispatch(setIsLoading(false));
        }

        // Save the device info inputs in the state
        setRegisterDeviceInputs((currentInputs) => {
            return produce(currentInputs, (draft) => {
                draft.stepCreateDevice = ({
                    deviceClass,
                    homeId,
                    channel: channelName,
                    deviceName,
                    timeZone,
                });
            });
        });
    }, [props.deviceRegistrationWizardSettings.deviceClassesSettings, props.projectId, uiDispatch, currentDeviceAndEntity.device,
        currentDeviceAndEntity.entity, appDataDispatch, dataDispatch, allDDPs, allDeviceConfigSchemas, cleanUpDDPs]);



    /**
     * On Step Configure Device completed
     */
    const onStepConfigureDeviceCompleted = useCallback(async (
        newDeviceConfigInputsForAllSchemas: readonly DeviceConfigInputs[],
    ) => {

        // To get to this step, a device must have already been created. If not, do nothing.
        const deviceId = currentDeviceAndEntity.device?.id;
        if (!deviceId || !allDeviceConfigSchemas) {
            return;
        }

        // Some time the user deletes input instances from repeatable param sets therefore we need to delete the DDPs associated with these
        // instances. We will keep track of the instances that were deleted and then delete them all at the end.
        const ddpKeysToBeDeleted: string[] = [];

        // Because we need to save the config in multiple DDPs, we will create and batch all the actions needed to write the config to DDP
        // and then call them in 1 promise
        const updateDeviceKVActions: Promise<void>[] = [];

        for (let schemaIndex = 0; schemaIndex < allDeviceConfigSchemas.length; schemaIndex += 1) {
            const selectedSchema = allDeviceConfigSchemas[schemaIndex];

            const oneDeviceConfigSchema = selectedSchema.deviceConfigSchema;
            const newDeviceConfigInputForOneSchema = newDeviceConfigInputsForAllSchemas[schemaIndex];
            const previousDeviceConfigInputForOneSchema = deviceConfigInputsForAllSchemas?.[schemaIndex];

            if (oneDeviceConfigSchema && newDeviceConfigInputForOneSchema) {
                createUpdateDeviceConfigDDPActions(
                    deviceId,
                    oneDeviceConfigSchema,
                    newDeviceConfigInputForOneSchema,
                    previousDeviceConfigInputForOneSchema,
                    allDDPs,
                    updateDeviceKVActions,
                    ddpKeysToBeDeleted,
                    appDataDispatch,
                );
            }
        }

        if (updateDeviceKVActions.length > 0) {
            // Dispatch all update DDP actions and wait for response
            await Promise.allSettled(updateDeviceKVActions);

            // Dispatch an action to refetch all deviceKVStore
            await appDataDispatch(fetchAllDeviceKVStores(deviceId));
        }

        // If there are DDP Keys to be deleted, call "deleteUnusedDDPs" to delete those DDPs
        if (ddpKeysToBeDeleted.length > 0) {
            await deleteUnusedDDPs(deviceId, ddpKeysToBeDeleted, trans, uiDispatch, appDataDispatch);
        }

    }, [allDDPs, allDeviceConfigSchemas, appDataDispatch, currentDeviceAndEntity.device?.id, deviceConfigInputsForAllSchemas, trans, uiDispatch]);


    /**
     * On Step Download Device Config completed
     */
    const onStepDownloadDeviceConfigCompleted = useCallback(() => {
        // TODO - Trigger save device config action
    }, []);



    /**
     * On Prev step button clicked. Take the user to the previous step but make sure the current step does not have any error
     */
    const onPrevStep = useCallback(() => {
        setCurrentStep((prevStep: number) => {
            return prevStep - 1;
        });
    }, []);


    /**
     * On Next step button clicked. Take the user to the next step but make sure the current step does not have any error
     */
    const onNextStep = useCallback(() => {
        const stepConfig = allSteps[currentStep];
        if (!stepConfig || !errorsByStepName[stepConfig.name]) {
            setCurrentStep((prevStep: number) => {
                return prevStep + 1;
            });
        }
    }, [currentStep, errorsByStepName]);



    /**
     * On go to the end clicked. Need to make sure none of the step contain errors
     */
    const onGotoLastStep = useCallback(() => {
        const hasErrors = Object.values(errorsByStepName).some((error) => {
            return error === true;
        });
        if (!hasErrors) {
            setCurrentStep(allSteps.length);
        }
    }, [errorsByStepName]);



    /**
     * On submit button clicked. We don't need to do anything.
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        return false;
    }, []);


    // Check if the user is done registering a device. This will be used for enable/disable the "Done" button
    // Because the "Download Config" step is not really required, we will set the flow as done after the user is done with the
    // Create device and configure device steps.
    const isDone = useMemo((): boolean => {
        return currentStep > 1;
    }, [currentStep]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className, sharedClasses.root)}
        >
            <div className={clsx(panelClasses.panelHeader)}>
                <div className={clsx(panelClasses.panelHeaderIconContainer)}>
                    {props.icon && <FontIcon className={clsx(panelClasses.panelHeaderIcon)} iconName={props.icon} />}
                </div>
                <div className={panelClasses.panelHeaderTitle}>
                    {props.title}
                </div>
            </div>
            <div className={clsx(panelClasses.panelContent, sharedClasses.panelContent)}>
                <form
                    className={
                        clsx(formClasses.root, props.formProps?.className)
                    }
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <Stepper
                        className={sharedClasses.stepper}
                        activeStep={currentStep}
                        orientation="vertical"
                    >
                        {allSteps.map((stepConfig: StepConfig, index: number) => {
                            return (
                                <Step className={sharedClasses.step} key={stepConfig.label}>
                                    <StepLabel
                                        className={sharedClasses.stepLabel}
                                        error={Boolean(errorsByStepName[stepConfig.name])}
                                        optional={errorsByStepName[stepConfig.name] ? trans('device_registration_wizard.generic_error_message') : ''}
                                        onClick={() => {
                                            onStepClick(index);
                                        }}
                                    >
                                        {capitalizeAll(trans(stepConfig.label))}
                                    </StepLabel>
                                    <StepContent className={sharedClasses.stepContent}>
                                        {stepConfig.name === RegisterDeviceStep.CREATE_DEVICE && (
                                            <DeviceRegistrationCreateDeviceStep
                                                projectId={props.projectId}
                                                targetDevice={currentDeviceAndEntity.device}
                                                targetEntity={currentDeviceAndEntity.entity}
                                                deviceRegistrationWizardSettings={props.deviceRegistrationWizardSettings}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === allSteps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepCreateDeviceCompleted}
                                                deviceClass={registerDeviceInputs.stepCreateDevice.deviceClass}
                                                homeId={registerDeviceInputs.stepCreateDevice.homeId}
                                                channelName={registerDeviceInputs.stepCreateDevice.channel}
                                                deviceName={registerDeviceInputs.stepCreateDevice.deviceName}
                                                timeZone={registerDeviceInputs.stepCreateDevice.timeZone}
                                            />
                                        )}

                                        {stepConfig.name === RegisterDeviceStep.CONFIGURE_DEVICE && currentDeviceAndEntity.device && (
                                            <DeviceRegistrationConfigureDeviceStep
                                                projectId={props.projectId}
                                                targetDevice={currentDeviceAndEntity.device}
                                                targetEntity={currentDeviceAndEntity.entity}
                                                deviceRegistrationWizardSettings={props.deviceRegistrationWizardSettings}
                                                allDeviceConfigSchemas={allDeviceConfigSchemas}
                                                allDDPs={allDDPs}
                                                deviceConfigInputsForAllSchemas={deviceConfigInputsForAllSchemas}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === allSteps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepConfigureDeviceCompleted}
                                            />
                                        )}

                                        {stepConfig.name === RegisterDeviceStep.DOWNLOAD_DEVICE_CONFIG && currentDeviceAndEntity.device && (
                                            <DeviceRegistrationDownloadDeviceConfigStep
                                                projectId={props.projectId}
                                                targetDevice={currentDeviceAndEntity.device}
                                                targetEntity={currentDeviceAndEntity.entity}
                                                deviceRegistrationWizardSettings={props.deviceRegistrationWizardSettings}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === allSteps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepDownloadDeviceConfigCompleted}
                                            />
                                        )}
                                    </StepContent>
                                </Step>
                            );
                        })}
                    </Stepper>

                    {props.isEditing && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div className={sharedClasses.allStepsCompletedMessage}>
                                {currentStep >= allSteps.length && (
                                    trans('device_registration_wizard.all_steps_completed_message')
                                )}
                            </div>
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        disabled={isDone}
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        disabled={!isDone}
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                        onClick={() => {
                                            if (props.actions?.saveUpdate) {
                                                props.actions.saveUpdate.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                </form>
            </div>
        </Paper>
    );
};
